import React, { useState, useEffect } from "react";
import { db } from "../../firebase-config";
import {
  collection, query, where, getDocs, orderBy, limit, serverTimestamp, addDoc
} from "firebase/firestore";
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Button, Dialog, DialogTitle, DialogContent, DialogActions, Typography
} from "@mui/material";
import Sidebar from "../../components/ui/Sidebar";
import PageHeader from "../../components/ui/PageHeader"; // ✅ Importing PageHeader

const Audits = () => {
  const [auditLogs, setAuditLogs] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedAudit, setSelectedAudit] = useState(null);
  const [openDetailDialog, setOpenDetailDialog] = useState(false);

  useEffect(() => {
    const fetchAuditLogs = async () => {
      try {
        const orgId = sessionStorage.getItem("organizationId");
        if (!orgId) {
          console.warn("No organization ID found.");
          return;
        }

        const auditsQuery = query(
          collection(db, "audits"),
          where("organizationId", "==", orgId),
          orderBy("timestamp", "desc"),
          limit(20)
        );

        const snapshot = await getDocs(auditsQuery);
        const auditsData = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        console.log("Fetched Audit Logs:", auditsData);
        setAuditLogs(auditsData);
      } catch (error) {
        console.error("Error fetching audit logs:", error);
        try {
          await addDoc(collection(db, "systemLogs"), {
            type: "Error",
            message: error.message,
            severity: "high",
            timestamp: serverTimestamp(),
          });
        } catch (logError) {
          console.error("Failed to log error in systemLogs:", logError);
        }
      }
    };

    fetchAuditLogs();
  }, []);

  /** Filter Audits by Search Term */
  const filteredAudits = auditLogs.filter((log) =>
    (log.action && log.action.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (log.email && log.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (log.adminEmail && log.adminEmail.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  /** Open Detail View */
  const handleOpenDetail = (audit) => {
    setSelectedAudit(audit);
    setOpenDetailDialog(true);
  };

  /** Close Detail View */
  const handleCloseDetail = () => {
    setSelectedAudit(null);
    setOpenDetailDialog(false);
  };

  return (
    <div className="dashboard-container">
      <Sidebar />
      <div className="dashboard-content">
        {/* ✅ New Page Header with Search Bar */}
        <PageHeader
          title="Audit Logs"
          showSearch={true}
          onSearch={(query) => setSearchTerm(query)}
        />

        {/* Audit Logs Table */}
        <TableContainer component={Paper}>
          {filteredAudits.length > 0 ? (
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Type</strong></TableCell>
                  <TableCell><strong>Performed By</strong></TableCell>
                  <TableCell><strong>Action</strong></TableCell>
                  <TableCell><strong>Timestamp</strong></TableCell>
                  <TableCell><strong>Details</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredAudits.map((log) => (
                  <TableRow key={log.id}>
                    <TableCell>
                      {log.adminEmail ? "Admin Action" : log.successfulImports ? "User Import" : "User Action"}
                    </TableCell>
                    <TableCell>{log.adminEmail || log.email || "N/A"}</TableCell>
                    <TableCell>{log.action || log.status || "N/A"}</TableCell>
                    <TableCell>
                      {log.timestamp?.seconds
                        ? new Date(log.timestamp.seconds * 1000).toLocaleString()
                        : "N/A"}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="outlined"
                        color="primary"
                        size="small"
                        onClick={() => handleOpenDetail(log)}
                      >
                        View Details
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <Typography variant="body2" color="textSecondary" style={{ padding: "20px" }}>
              No audit logs found.
            </Typography>
          )}
        </TableContainer>

        {/* Audit Details Dialog */}
        <Dialog open={openDetailDialog} onClose={handleCloseDetail}>
          <DialogTitle>Audit Details</DialogTitle>
          <DialogContent>
            {selectedAudit ? (
              <div>
                <Typography><strong>Type:</strong> {selectedAudit.adminEmail ? "Admin Action" : selectedAudit.successfulImports ? "User Import" : "User Action"}</Typography>
                <Typography><strong>Performed By:</strong> {selectedAudit.adminEmail || selectedAudit.email || "N/A"}</Typography>
                <Typography><strong>Action:</strong> {selectedAudit.action || selectedAudit.status || "N/A"}</Typography>
                <Typography><strong>Timestamp:</strong> {selectedAudit.timestamp?.seconds ? new Date(selectedAudit.timestamp.seconds * 1000).toLocaleString() : "N/A"}</Typography>

                {/* Special Cases for Different Audit Types */}
                {selectedAudit.affectedUser && (
                  <Typography><strong>Affected User:</strong> {selectedAudit.affectedUser}</Typography>
                )}

                {selectedAudit.successfulImports && (
                  <div>
                    <Typography variant="h6">Successful Imports</Typography>
                    {selectedAudit.successfulImports.map((user, index) => (
                      <Typography key={index}>✅ {user.email} - {user.message}</Typography>
                    ))}
                  </div>
                )}

                {selectedAudit.failedImports && (
                  <div>
                    <Typography variant="h6">Failed Imports</Typography>
                    {selectedAudit.failedImports.map((user, index) => (
                      <Typography key={index}>❌ {user.email} - {user.error}</Typography>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <Typography>No details available.</Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDetail} color="primary">
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    </div>
  );
};

export default Audits;